import { useState } from 'react';
import { generatePassword, type ItemFields } from '@battini/crypto';
import { useApp } from '../store/AppContext.js';
import { Modal } from './ui.js';
import { PasswordStrength } from './PasswordStrength.js';
import type { DecryptedItem } from '../crypto/session.js';

interface Props {
  onClose: () => void;
  editing?: DecryptedItem | null;
}

export function VaultItemModal({ onClose, editing }: Props) {
  const { addItem, updateItem } = useApp();
  const [title, setTitle] = useState(editing?.title ?? '');
  const [username, setUsername] = useState(editing?.username ?? '');
  const [password, setPassword] = useState(editing?.password ?? '');
  const [url, setUrl] = useState(editing?.url ?? '');
  const [notes, setNotes] = useState(editing?.notes ?? '');
  const [tags, setTags] = useState((editing?.tags ?? []).join(', '));
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const gen = () => {
    setPassword(generatePassword({ length: 24, uppercase: true, lowercase: true, numbers: true, symbols: true, avoidAmbiguous: true }));
    setShowPw(true);
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      if (!title.trim()) throw new Error('Title is required');
      const fields: ItemFields = {
        title: title.trim(),
        username: username || undefined,
        password: password || undefined,
        url: url || undefined,
        notes: notes || undefined,
        tags: tags.split(',').map((t) => t.trim()).filter(Boolean),
      };
      if (editing) await updateItem(editing.id, fields, editing.version);
      else await addItem(fields);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={editing ? 'Edit item' : 'Add item'} onClose={onClose}>
      <div className="field">
        <label>Title *</label>
        <input value={title} onChange={(e) => setTitle(e.target.value)} autoFocus placeholder="e.g. GitHub" />
      </div>
      <div className="row2">
        <div className="field">
          <label>Username / email</label>
          <input value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="off" />
        </div>
        <div className="field">
          <label>Password</label>
          <div className="password-input">
            <input type={showPw ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="off" />
            <button type="button" className="toggle" onClick={() => setShowPw((s) => !s)}>{showPw ? 'hide' : 'show'}</button>
          </div>
          {password && <div className="mt-sm"><PasswordStrength password={password} /></div>}
        </div>
      </div>
      <div className="flex gap-sm mb">
        <button type="button" className="btn secondary sm" onClick={gen}>🎲 Generate strong</button>
      </div>
      <div className="field">
        <label>Website URL</label>
        <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://example.com" />
      </div>
      <div className="field">
        <label>Tags (comma separated)</label>
        <input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="work, finance" />
      </div>
      <div className="field">
        <label>Notes</label>
        <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </div>
      {error && <div className="alert error">{error}</div>}
      <div className="flex between mt">
        <button className="btn ghost" onClick={onClose}>Cancel</button>
        <button className="btn" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save item'}</button>
      </div>
    </Modal>
  );
}
